import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { CircleUserRound, LogIn } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiScopes, nameOf, rolesIn } from './auth'
import { Menu, MenuRow, menuItem } from './Menu'
import type { AppConfig } from './config'

/**
 * Who is signed in, and the way out. The icon alone in the masthead -- no name beside it -- with
 * the name inside the menu, where it answers the question the icon raises rather than taking a
 * width that varies with whoever signed in.
 *
 * Signed out, the same slot signs you in directly. A menu holding one item called Sign in is a
 * click in front of a click.
 */
export function AccountMenu({ config }: { config: AppConfig }) {
  const { instance, accounts, inProgress } = useMsal()
  const [failure, setFailure] = useState<string | null>(null)
  const [roles, setRoles] = useState<readonly string[]>([])
  const account = accounts[0]

  // Silently, so this is MSAL's cache rather than a round trip: the same token every screen
  // is already sending. A token that cannot be had is no roles -- the menu says what this
  // sign-in is worth, and "worth nothing yet" is the honest answer to a refused grant.
  useEffect(() => {
    if (!account) return
    let live = true

    instance
      .acquireTokenSilent({ scopes: apiScopes(config), account })
      .then((token) => live && setRoles(rolesIn(token.accessToken)))
      .catch(() => live && setRoles([]))

    return () => {
      live = false
    }
  }, [instance, account, config])

  if (!account) {
    return (
      <div className="relative">
        <button
          type="button"
          aria-label="Sign in"
          title="Sign in"
          disabled={inProgress !== InteractionStatus.None}
          onClick={() => {
            setFailure(null)
            instance
              .loginRedirect({ scopes: apiScopes(config) })
              .catch((error: unknown) => setFailure(String(error)))
          }}
          className="flex rounded-md p-1.5 text-green-800 hover:bg-green-050 disabled:opacity-55"
        >
          <LogIn aria-hidden="true" />
        </button>

        {/* A sign-in that failed from an icon button would otherwise fail silently, and the
            next thing the Admin does is press it again. */}
        {failure && (
          <p
            role="alert"
            className="absolute right-0 top-full z-10 mt-2 min-w-56 rounded-lg border border-ink-100 bg-card p-3 text-red-700"
          >
            {failure}
          </p>
        )}
      </div>
    )
  }

  return (
    <Menu label="Account" icon={<CircleUserRound aria-hidden="true" />}>
      <MenuRow>
        <p className="px-3 py-2">
          <span className="font-bold">{nameOf(account)}</span>
          <br />
          {/* What this sign-in claims, not what the API decides -- app roles ride the access
              token and the API reads them there. Said quietly, because it is diagnostic. */}
          <span className="text-muted">{roles.length > 0 ? roles.join(', ') : 'no roles here'}</span>
        </p>
      </MenuRow>
      <MenuRow>
        <button
          type="button"
          onClick={() => {
            instance.logoutRedirect().catch((error: unknown) => setFailure(String(error)))
          }}
          className={menuItem}
        >
          Sign out
        </button>
      </MenuRow>
      {failure && (
        <MenuRow>
          <p role="alert" className="px-3 py-2 text-red-700">
            {failure}
          </p>
        </MenuRow>
      )}
    </Menu>
  )
}
